import { create } from "zustand";
import type { ActivityActor } from "src/lib/activityService";
import {
  getLiveCarouselParallaxEnabled,
  setLiveCarouselParallaxEnabled,
} from "src/lib/happeningNowService";

/**
 * Parallax mode for the live carousel. Source of truth is the
 * `happeningNow/live` document (`parallaxEnabled` field) — read by mobile
 * alongside the carousel. Previously an RTDB feature flag; see
 * `happeningNowService.setLiveCarouselParallaxEnabled`.
 */
export type HappeningNowParallaxStore = {
  enabled: boolean;
  loading: boolean;
  updating: boolean;

  load: () => Promise<void>;
  setEnabled: (enabled: boolean, actor?: ActivityActor | null) => Promise<void>;
  reset: () => void;
};

const initialState = {
  enabled: false,
  loading: false,
  updating: false,
} as const;

export const useHappeningNowParallaxStore = create<HappeningNowParallaxStore>(
  (set) => ({
    ...initialState,

    load: async () => {
      set({ loading: true });
      try {
        const enabled = await getLiveCarouselParallaxEnabled();
        set({ enabled });
      } catch (err: unknown) {
        console.error("Failed to load parallax setting:", err);
      } finally {
        set({ loading: false });
      }
    },

    setEnabled: async (enabled, actor) => {
      set({ updating: true });
      try {
        await setLiveCarouselParallaxEnabled(enabled, actor);
        set({ enabled });
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : "Failed to update parallax mode.";
        alert(message);
        throw err;
      } finally {
        set({ updating: false });
      }
    },

    reset: () => set({ ...initialState }),
  }),
);
